
import { Transaction, PaginationMeta } from "@/types";
import TransactionItem from "./TransactionItem";
import Pagination from "./Pagination";
import { useLanguage } from "@/contexts/LanguageContext";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface TransactionListProps {
  transactions: Transaction[];
  isLoading: boolean;
  meta?: PaginationMeta;
  onPageChange: (page: number) => void;
  currentPage: number;
}

const TransactionList = ({
  transactions,
  isLoading,
  meta,
  onPageChange,
  currentPage
}: TransactionListProps) => {
  const { t } = useLanguage();
  
  if (isLoading) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">{t("transactions.loading")}</p>
      </div>
    );
  }
  
  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
        <h3 className="text-xl font-medium text-gray-900 mb-2">{t("transactions.noTransactions")}</h3>
        <p className="text-gray-500">{t("common.noData")}</p>
      </div>
    );
  }
  
  return (
    <div className="mt-6">
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-sm font-semibold text-gray-700">{t("common.from")}</TableHead>
              <TableHead className="p-0 w-10 text-center"></TableHead>
              <TableHead className="text-sm font-semibold text-gray-700">{t("common.to")}</TableHead>
              <TableHead className="text-sm font-semibold text-gray-700">{t("common.date")}</TableHead>
              <TableHead className="text-sm font-semibold text-gray-700">{t("transactions.transactionId")}</TableHead>
              <TableHead className="text-sm font-semibold text-gray-700">{t("common.status")}</TableHead>
              <TableHead className="text-sm font-semibold text-gray-700 text-right">{t("common.credits")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.map(transaction => (
              <TransactionItem key={transaction.id} transaction={transaction} />
            ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="mt-6 flex justify-between items-center">
        <div className="flex-1"></div>
        {meta && meta.pages > 1 && (
          <Pagination 
            currentPage={currentPage} 
            totalPages={meta.pages} 
            onPageChange={onPageChange} 
            className="flex-1 flex justify-center" 
          />
        )}
        {meta && (
          <div className="flex-1 text-sm text-gray-500 text-right">
            {t("transactions.showing")} {meta.offset + 1}-{Math.min(meta.offset + transactions.length, meta.total)} {t("transactions.of")} {meta.total} {t("transactions.transactions")}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionList;
